<template>
  <Dialog
    v-model:visible="localVisible"
    modal
    :style="{ width: '50rem', marginLeft: '2rem', marginRight: '2rem' }"
    :closable="false"
  >
    <template #header>
      <div class="flex items-center gap-4">
        <Transition name="fade-collapse">
          <div
            v-if="isLoading"
            class="spinner-container"
          >
            <ProgressSpinner
              style="width: 50px; height: 50px; flex-shrink: 0"
              strokeWidth="8"
              fill="transparent"
              animationDuration=".5s"
              aria-label="Loading Progress"
            />
          </div>
        </Transition>
        <Transition name="fade">
          <span class="text-2xl">{{ headerMsg }}</span>
        </Transition>
      </div>
    </template>
    <div
      v-if="showError"
      class="text-red-500"
    >
      {{ error_message }}
    </div>
    <SettingsTiles></SettingsTiles>
  </Dialog>
</template>

<script setup>
  import { ref, watch, onUnmounted } from "vue";
  import SettingsTiles from "../pages/settings/SettingsTiles.vue";
  import ProgressSpinner from "primevue/progressspinner";
  import Constants from "@/utils/constants";
  import { useAppSettingsStore } from "@/stores/appSettingsStore";

  const props = defineProps({
    headerMsg: {
      type: String,
      required: true
    },
    visible: {
      type: Boolean,
      required: true
    },
    error_message: {
      type: String,
      required: true
    },
    isLoading: {
      type: Boolean,
      required: true
    }
  });

  const emit = defineEmits(["update:visible"]);

  const localVisible = ref(props.visible);
  const showError = ref(false);
  let timeoutId = null;

  // Watch the visible prop and update localVisible accordingly
  watch(
    () => props.visible,
    (newVal) => {
      localVisible.value = newVal;
    },
    { immediate: true }
  );

  // Watch localVisible and emit changes to the parent
  watch(
    () => localVisible.value,
    (newVal) => {
      emit("update:visible", newVal);
    }
  );

  // Watch isLoading to handle timeout
  watch(
    () => props.isLoading,
    async (newVal) => {
      if (newVal) {
        const timeout = await useAppSettingsStore().getAppSettings(
          Constants.enums.APP_SETTINGS.SYNC_TIMEOUT
        );
        console.log("timeout :", timeout);
        // Start loading - set up timeout
        showError.value = false;
        timeoutId = setTimeout(() => {
          if (props.isLoading) {
            showError.value = true;
          }
        }, timeout);
      } else {
        // Loading completed - clear timeout
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
        showError.value = false;
      }
    },
    { immediate: true }
  );

  // Clean up timeout on component unmount
  onUnmounted(() => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  });

  const closeDialog = () => {
    localVisible.value = false;
  };

  const confirmAction = () => {
    props.onConfirm();
    closeDialog();
  };
</script>

<style scoped>
  .spinner-container {
    width: 50px;
    height: 50px;
    flex-shrink: 0;
  }

  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.3s ease;
  }

  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
  }

  .fade-collapse-enter-active,
  .fade-collapse-leave-active {
    transition: all 0.3s ease;
  }

  .fade-collapse-enter-from,
  .fade-collapse-leave-to {
    opacity: 0;
    width: 0;
    height: 0;
    margin: 0;
    padding: 0;
  }
</style>
